import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { APIError, useAxios } from '../core/_axios';
import QRCode from 'react-qr-code';
import { submitRegistrationRequest } from '../core/_requests';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

const linkUrl = process.env.NODE_ENV === 'production' ?
    'https://link.signatureguestbook.com' :
    'https://signature-guestbook-link.ngrok.io'

const QR_CODE_URL = `${linkUrl}/l/app/auth/register`;

// reCAPTCHA Enterprise site key
const RECAPTCHA_SITE_KEY = '6LeKzhQsAAAAAGlinDX1qnJBCLzdmrw5b0lB1wyH';

// Declare grecaptcha type for Enterprise
declare global {
    interface Window {
        grecaptcha: {
            enterprise: {
                ready: (callback: () => void) => void;
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
            };
        };
    }
}

/**
 * Get reCAPTCHA token - in production uses real reCAPTCHA, in development simulates
 * the async behavior to catch timing-related bugs locally.
 *
 * This ensures the same code path runs in both environments, catching issues like
 * React synthetic event pooling (e.g., e.currentTarget becoming null after await).
 */
const getRecaptchaToken = async (): Promise<string | null> => {
    if (process.env.NODE_ENV === 'production') {
        // Production: use real reCAPTCHA Enterprise
        await new Promise<void>((resolve) => {
            window.grecaptcha.enterprise.ready(() => resolve());
        });

        return await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
            action: 'REGISTER'
        });
    } else {
        // Development: simulate async behavior to catch timing bugs
        // This ensures we test the same code paths as production
        await new Promise<void>((resolve) => setTimeout(resolve, 50));
        return null; // No token needed for local dev
    }
};

const Register = () => {
    // Get localized copy
    const { copy } = useLocalizedCopy();
    
    // Auth context, if needed
    const { login } = useAuth();

    // For making API calls
    const axios = useAxios();

    // For navigation (if needed)
    const navigate = useNavigate();

    // Form fields
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [funeralHomeName, setFuneralHomeName] = useState('');
    const [funeralHomeCityState, setFuneralHomeCityState] = useState('');

    // Submission state
    const [submitting, setSubmitting] = useState(false);
    const [errorSubmitting, setErrorSubmitting] = useState<string | null>(null);

    // NEW: Toggle state to switch between form and QR code view
    const [viewingItem, setViewingItem] = useState<'form' | 'qrCode'>('form');

    const [successSubmitting, setSuccessSubmitting] = useState(false);

    // Load reCAPTCHA Enterprise script
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
            script.async = true;
            document.head.appendChild(script);

            return () => {
                document.head.removeChild(script);
            };
        }
    }, []);

    const handleRegister = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (submitting) {
            return;
        }

        // IMPORTANT: Capture form reference before any async operations
        // React's synthetic events are pooled and e.currentTarget becomes null after await
        const formElement = e.currentTarget;

        // Get reCAPTCHA token (real in prod, simulated async in dev to catch timing bugs)
        let recaptchaToken: string | null = null;
        try {
            recaptchaToken = await getRecaptchaToken();
        } catch (error) {
            console.error('reCAPTCHA error:', error);
            setErrorSubmitting('Failed to verify reCAPTCHA. Please try again.');
            return;
        }

        setSubmitting(true);
        setErrorSubmitting(null);
        try {
            const formData = new FormData(formElement);
            // Add reCAPTCHA token to form data (only present in production)
            if (recaptchaToken) {
                formData.append('recaptchaToken', recaptchaToken);
            }
            await submitRegistrationRequest(axios, formData);
            const cleanedAndDowncasedEmail = emailAddress.trim().toLowerCase();
            navigate(`/auth/login-confirm?e=${encodeURIComponent(cleanedAndDowncasedEmail)}`);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorSubmitting(errorMessage);
        } finally {
            setSubmitting(false);
        }
    }, [submitting, axios, emailAddress, navigate]);

    // Example function to download the rendered QR code
    const onDownloadQRCode = useCallback(() => {
        // Look for the first SVG inside a .qr-code-wrapper container
        const qrCodeSVG = document.querySelector<HTMLDivElement>('.qr-code-wrapper > svg');
        if (!qrCodeSVG) {
            alert('No QR code found.');
            return;
        }

        // Create a new canvas element
        const canvas = document.createElement('canvas');
        const size = 512; // Canvas size
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Give a white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);

        // Convert SVG to Base64 data URL and draw onto canvas
        const img = new Image();
        img.src = `data:image/svg+xml;base64,${btoa(qrCodeSVG.outerHTML)}`;
        img.onload = () => {
            ctx.drawImage(img, 0, 0, size, size);

            // Trigger a download
            const a = document.createElement('a');
            a.download = 'qr-code.png';
            a.href = canvas.toDataURL('image/png');
            a.click();
        };
    }, []);

    if (successSubmitting) {
        return (
            <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
                <div className="card max-w-[420px] w-full m-8">
                    <div className="card-body flex flex-col gap-5 p-10">
                        <div className="text-center mb-2.5">
                            <div className="flex items-center justify-center mb-5">
                                <img
                                    className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                    src="/design/web_logo_black_small.png"
                                    alt="Small Logo"
                                />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                                {copy.Auth.register.accountCreated}
                            </h3>
                            <p>
                                {copy.Common.messages.saved}
                            </p>
                            <p className="mt-2">
                                {copy.Auth.register.verifyEmail}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
            <div className="card max-w-[420px] w-full m-8">
                <div className="card-body flex flex-col gap-5 p-10">
                    {/* Always show the same header (logo + title), regardless of view */}
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-5">
                            <img
                                className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                src="/design/web_logo_black_small.png"
                                alt="Small Logo"
                            />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                            {copy.Auth.register.title}
                        </h3>
                        {true && <div className="flex items-center justify-center font-medium">
                            <span className="text-2sm text-gray-600 me-1.5">
                                {copy.Auth.register.haveAccount}
                            </span>
                            <Link className="text-2sm link" to="/auth/login">
                                {copy.Auth.register.loginLink}
                            </Link>
                        </div>}
                    </div>

                    {/* Switch view link in the top-right (or wherever you prefer) */}
                    <div className="flex justify-end mb-2">
                        {viewingItem === 'qrCode' ? (
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => setViewingItem('form')}
                            >
                                {copy.Common.buttons.view} Form
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => setViewingItem('qrCode')}
                            >
                                {copy.Common.buttons.view} QR Code
                            </button>
                        )}
                    </div>

                    {viewingItem === 'form' ? (
                        // ---------------------
                        // FORM VIEW
                        // ---------------------
                        <form
                            id="register_form"
                            onSubmit={handleRegister}
                        >
                            {/* Full Name */}
                            <div className="flex flex-col gap-1 mb-4">
                                <label className="form-label text-gray-900">{copy.Common.labels.name}</label>
                                <input
                                    id="name"
                                    name="name"
                                    className="input"
                                    placeholder={`${copy.Auth.register.firstNamePlaceholder} ${copy.Auth.register.lastNamePlaceholder}`}
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>

                            {/* Phone Number */}
                            <div className="flex flex-col gap-1 mb-4">
                                <label className="form-label text-gray-900">{copy.Common.labels.phone}</label>
                                <input
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    className="input"
                                    placeholder={copy.Public.sign.phonePlaceholder || "(555) 555-5555"}
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Email Address */}
                            <div className="flex flex-col gap-1 mb-4">
                                <label className="form-label text-gray-900">{copy.Auth.register.emailLabel}</label>
                                <input
                                    id="emailAddress"
                                    name="emailAddress"
                                    className="input"
                                    placeholder={copy.Auth.register.emailPlaceholder}
                                    type="email"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Funeral Home Name */}
                            <div className="flex flex-col gap-1 mb-4">
                                <label className="form-label text-gray-900">{copy.Account.details.businessName}</label>
                                <input
                                    id="funeralHomeName"
                                    name="funeralHomeName"
                                    className="input"
                                    placeholder="Solemn Oaks"
                                    type="text"
                                    value={funeralHomeName}
                                    onChange={(e) => setFuneralHomeName(e.target.value)}
                                    required
                                />
                            </div>

                            {/* City and State (single-line) */}
                            <div className="flex flex-col gap-1 mb-4">
                                <label className="form-label text-gray-900">
                                    {copy.Common.labels.cityAndState}
                                </label>
                                <input
                                    id="funeralHomeCityState"
                                    name="funeralHomeCityState"
                                    className="input"
                                    placeholder={`${copy.Common.labels.city}, ${copy.Common.labels.state}`}
                                    type="text"
                                    value={funeralHomeCityState}
                                    onChange={(e) => setFuneralHomeCityState(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Submit Button */}
                            <button
                                className="btn btn-primary flex justify-center w-full"
                                type="submit"
                                disabled={submitting}
                            >
                                {!submitting ? copy.Auth.register.registerButton : copy.Auth.register.registering}
                            </button>

                            {/* Error Message */}
                            {errorSubmitting && (
                                <div>
                                    <div
                                        className="text-center p-4 mt-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400"
                                        role="alert"
                                    >
                                        {errorSubmitting}
                                    </div>
                                </div>
                            )}
                        </form>
                    ) : (
                        // ---------------------
                        // QR CODE VIEW
                        // ---------------------
                        <div>

                            <div className="flex flex-col gap-5 items-center">
                                {/* The actual QR code */}
                                <div className="qr-code-wrapper bg-white p-4 inline-block">
                                    {/* show the current URL */}
                                    <QRCode
                                        value={QR_CODE_URL}
                                        size={256}
                                    />
                                </div>
                            </div>

                            {/* Buttons below the QR code */}
                            <div className="flex gap-3 mt-4 justify-center">
                                <button
                                    onClick={onDownloadQRCode}
                                    type="button"
                                    className="btn btn-light"
                                >
                                    {copy.Common.buttons.download} QR Code
                                </button>
                                <button
                                    className="btn"
                                    onClick={() => setViewingItem('form')}
                                >
                                    {copy.Common.buttons.cancel}
                                </button>
                            </div>
                            <div className="text-center mt-4">
                                <a
                                    href={QR_CODE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-link"
                                >
                                    QR Code Link
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;
